import { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./Manage.module.css";
import LoginStatus from "../../Components/User/LoginStatus/LoginStatus";
import BarLoader from "../../UI/Loaders/BarLoader/BarLoader";
import { ErrorBoundary } from "../../HOC/ErrorHandling/ErrorBoundary/ErrorBoundary";
import Footer from "../../Components/Footer/Footer";
import { useRunGatherCatalogData } from "../../Hooks/useRunGatherCatalogData";
import { useRunGatherContentData } from "../../Hooks/useRunGatherContentData";
import { AdminUIProvider } from "./Admin/AdminUIContext";
import AdminToast from "./Admin/AdminToast";
import AdminConfirmModal from "./Admin/AdminConfirmModal";
import { SIDEBAR_STORAGE_KEY, SIDEBAR_SECTION_KEY } from "./Admin/adminUtils";

const NAV_ITEMS = [
  {
    to: "/manage",
    end: true,
    label: "Dashboard",
    match: (p) => p === "/manage",
  },
  {
    to: "/manage/catalog",
    label: "Catalog",
    match: (p) => p.startsWith("/manage/catalog"),
  },
  {
    to: "/manage/content",
    label: "Content",
    match: (p) => p.startsWith("/manage/content"),
  },
  {
    to: "/manage/metadata",
    label: "Metadata",
    match: (p) => p.startsWith("/manage/metadata"),
  },
  {
    to: "/manage/tracking",
    label: "Tracking",
    match: (p) => p.startsWith("/manage/tracking"),
  },
  {
    to: "/manage/tools",
    label: "Tools",
    match: (p) => p.startsWith("/manage/tools"),
  },
  {
    to: "/manage/help",
    label: "Help",
    match: (p) => p.startsWith("/manage/help"),
  },
];

const Manage = (props) => {
  const user = useSelector((state) => state.auth.user);
  const loadingStatus = useSelector(
    (state) => state.loadingRequests.pendingLoadRequests,
  );
  const location = useLocation();
  const runGatherCatalogData = useRunGatherCatalogData();
  const runGatherContentData = useRunGatherContentData();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const refreshData = useCallback(() => {
    const setLocalError = props.setNoDBErrors
      ? (err) => {
          if (err?.active) {
            props.setNoDBErrors({
              status: false,
              message: err.message || "Error loading data",
            });
          }
        }
      : () => {};
    runGatherCatalogData({ user, setLocalError });
    runGatherContentData({ user, setLocalError });
  }, [user, runGatherCatalogData, runGatherContentData, props]);

  useEffect(() => {
    const handler = () => refreshData();
    window.addEventListener("ms1-admin-refresh-data", handler);
    return () => window.removeEventListener("ms1-admin-refresh-data", handler);
  }, [refreshData]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarCollapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SIDEBAR_SECTION_KEY, location.pathname);
    } catch {
      /* ignore */
    }
    setMobileNavOpen(false);
  }, [location.pathname]);

  /* eslint eqeqeq: 0 */
  const isAdmin = user && user.isAdmin == true;

  return (
    <AdminUIProvider>
      <div
        className={`${styles["admin-root"]} ${
          sidebarCollapsed ? styles["sidebar-collapsed"] : ""
        }`}
      >
        {loadingStatus > 0 && (
          <div className="bar-loader-container">
            <div className="bar-loader-wrap">
              <BarLoader />
            </div>
          </div>
        )}

        <header className={styles["admin-topbar"]}>
          <div className={styles["topbar-left"]}>
            <button
              type="button"
              className={styles["icon-btn"]}
              aria-label={
                mobileNavOpen ? "Close navigation" : "Open navigation"
              }
              onClick={() => setMobileNavOpen((o) => !o)}
            >
              ☰
            </button>
            <button
              type="button"
              className={`${styles["icon-btn"]} ${styles["desktop-only"]}`}
              aria-label={
                sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
              onClick={() => setSidebarCollapsed((c) => !c)}
            >
              {sidebarCollapsed ? "»" : "«"}
            </button>
          </div>
          <div className={styles["topbar-center"]}>
            {" "}
            <div className={styles["brand-block"]}>
              <span className={styles["brand-kicker"]}>Admin</span>
              <span className={styles["brand-title"]}>
                Ignite <span className={styles["brand-accent"]}>Elysium</span>
              </span>
            </div>{" "}
          </div>
          <div className={styles["topbar-right"]}>
            {isAdmin && (
              <button
                type="button"
                className={styles["topbar-action"]}
                onClick={() => {
                  refreshData();
                }}
              >
                Refresh
              </button>
            )}
            {user && (
              <span className={styles["user-chip"]} title={user.email}>
                {user.userName || user.email}
                {isAdmin ? " · admin" : ""}
              </span>
            )}
          </div>
        </header>

        {!isAdmin && (
          <div className={styles["lock-screen"]}>
            <div className={styles["lock-card"]}>
              <header className={styles["lock-header"]}>
                <p className={styles["lock-kicker"]}>Ignite Elysium</p>
                <h1 className={styles["lock-title"]}>Administrator access</h1>
                {!user && (
                  <p className={styles["lock-copy"]}>
                    Sign in with an administrator account to manage catalog and
                    site content.
                  </p>
                )}
              </header>

              {user && user.isAdmin != true && (
                <div className={styles["lock-alert"]} role="status">
                  <strong className={styles["lock-alert-title"]}>
                    Signed in without admin rights
                  </strong>
                  <p className={styles["lock-alert-body"]}>
                    <span className={styles["lock-alert-user"]}>
                      {user.userName || user.email}
                    </span>{" "}
                    does not have administrator privileges. Contact the site
                    admin if you believe this is an error, or log out and try a
                    different account.
                  </p>
                </div>
              )}

              <ErrorBoundary>
                <section
                  className={styles["login-wrap"]}
                  aria-label={user ? "Account session" : "Sign in"}
                >
                  {!user && (
                    <h2 className={styles["login-section-title"]}>Sign in</h2>
                  )}
                  {user && user.isAdmin != true && (
                    <h2 className={styles["login-section-title"]}>Session</h2>
                  )}
                  <LoginStatus user={props.user} variant="admin" />
                </section>
              </ErrorBoundary>

              <footer className={styles["lock-footer"]}>
                <a href="/" className={styles["lock-footer-link"]}>
                  ← Back to public site
                </a>
              </footer>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className={styles["admin-body"]}>
            {mobileNavOpen && (
              <button
                type="button"
                className={styles["nav-scrim"]}
                aria-label="Close menu"
                onClick={() => setMobileNavOpen(false)}
              />
            )}
            <aside
              className={`${styles["admin-sidebar"]} ${
                mobileNavOpen ? styles["sidebar-open"] : ""
              }`}
            >
              <nav className={styles["side-nav"]} aria-label="Admin">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `${styles["nav-link"]} ${
                        isActive || item.match(location.pathname)
                          ? styles["nav-link-active"]
                          : ""
                      }`
                    }
                  >
                    <span className={styles["nav-label"]}>{item.label}</span>
                  </NavLink>
                ))}
              </nav>
              <div className={styles["sidebar-footer"]}>
                <a href="/" className={styles["nav-link"]}>
                  ← Public
                </a>
              </div>
            </aside>

            <main className={styles["admin-main"]}>
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </main>
          </div>
        )}

        <div className={styles["admin-footer"]}>
          <ErrorBoundary>
            <Footer />
          </ErrorBoundary>
        </div>

        <AdminToast />
        <AdminConfirmModal />
      </div>
    </AdminUIProvider>
  );
};

export default Manage;
